import { logger } from "@/utils/logger";
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { DealerManager } from '@/utils/dealerManager';
import { supabase as mainSupabase } from './client';

// Get Supabase configuration from centralized config
import { supabaseUrl, supabaseAnonKey } from './config';

const SUPABASE_CONFIG = {
  url: supabaseUrl,
  anonKey: supabaseAnonKey
};

// Create base Supabase client for secured operations by reusing main client configuration
const baseSupabase = mainSupabase;

/**
 * Enhanced Supabase client that automatically includes dealer context headers
 * for improved security while maintaining backward compatibility
 */
class SecuredSupabaseClient {
  private baseClient: SupabaseClient<Database>;

  constructor() {
    this.baseClient = baseSupabase;
  }

  /**
   * Get headers with dealer context for improved security
   */
  private async getDealerHeadersAsync(): Promise<Record<string, string>> {
    try {
      // First try to get dealer info from the manager
      let dealerInfo = DealerManager.getDealerInfo();
      
      // If no dealer info cached, try to initialize
      if (!dealerInfo) {
        logger.log(`🔧 SecuredSupabase: No dealer context cached, attempting initialization...`);
        try {
          await DealerManager.initializeFromGlobalSettings();
          dealerInfo = DealerManager.getDealerInfo();
        } catch (initError) {
          logger.warn('🔧 SecuredSupabase: Failed to initialize dealer context:', initError);
        }
      }
      
      if (dealerInfo) {
        logger.log(`🔧 SecuredSupabase: Using dealer context: ID=${dealerInfo.dealer_id}, Domain=${dealerInfo.domain}`);
        return {
          'x-dealer-id': dealerInfo.dealer_id.toString(),
          'x-dealer-domain': dealerInfo.domain,
        };
      }
      
      // Fallback: try to infer from current domain for active requests
      const currentDomain = window.location.hostname;
      logger.log(`🔧 SecuredSupabase: No dealer context available, current domain: ${currentDomain}`);
      
      return {};
    } catch (error) {
      logger.warn('Could not get dealer context for headers:', error);
      return {};
    }
  }

  private getDealerHeaders(): Record<string, string> {
    try {
      const dealerInfo = DealerManager.getDealerInfo();
      
      if (dealerInfo) {
        // CRITICAL: Log what dealer context we're actually using
        logger.log(`🔧 SecuredSupabase: Using dealer context: ID=${dealerInfo.dealer_id}, Domain=${dealerInfo.domain}`);
        logger.log(`🔧 SecuredSupabase: Current window location: ${window.location.hostname}`);
        
        // SECURITY CHECK: Warn if there's a domain mismatch
        if (window.location.hostname !== dealerInfo.domain && 
            !window.location.hostname.includes('lovable') && 
            !dealerInfo.domain.includes('lovable')) {
          logger.warn(`🚨 SecuredSupabase: DOMAIN MISMATCH! Window: ${window.location.hostname}, Dealer: ${dealerInfo.domain}`);
        }
        
        return {
          'x-dealer-id': dealerInfo.dealer_id.toString(),
          'x-dealer-domain': dealerInfo.domain,
        };
      }
      
      return {};
    } catch (error) {
      logger.warn('Could not get dealer context for headers:', error);
      return {};
    }
  }

  /**
   * Direct access to base client for all operations
   */
  get raw() {
    return this.baseClient;
  }

  /**
   * Enhanced functions.invoke() method with dealer context
   */
  async invokeFunctionWithContext<T = any>(
    functionName: string,
    options: {
      body?: any;
      headers?: Record<string, string>;
      method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
    } = {}
  ): Promise<{ data: T; error: any }> {
    logger.log(`🔧 SecuredSupabase: Invoking function '${functionName}' with options:`, options);
    
    const dealerHeaders = this.getDealerHeaders();
    logger.log(`🔧 SecuredSupabase: Dealer headers:`, dealerHeaders);
    
    const combinedHeaders = {
      ...dealerHeaders,
      ...options.headers,
    };
    logger.log(`🔧 SecuredSupabase: Combined headers:`, combinedHeaders);

    const result = await this.baseClient.functions.invoke(functionName, {
      ...options,
      headers: combinedHeaders,
    });
    
    logger.log(`🔧 SecuredSupabase: Function '${functionName}' result:`, result);
    return result;
  }

  /**
   * Access to functions namespace with context
   */
  get functions() {
    return {
      invoke: this.invokeFunctionWithContext.bind(this),
    };
  }

  /**
   * Auth methods (unchanged)
   */
  get auth() {
    return this.baseClient.auth;
  }

  /**
   * Storage methods (unchanged)  
   */
  get storage() {
    return this.baseClient.storage;
  }

  /**
   * From method with dealer context headers
   */
  from<T extends keyof Database['public']['Tables']>(table: T) {
    const dealerHeaders = this.getDealerHeaders();
    
    const queryBuilder = this.baseClient.from(table);
    
    // For other tables, ensure we always set headers
    if (Object.keys(dealerHeaders).length > 0) {
      const headersToSet = {
        ...dealerHeaders,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      
      queryBuilder.headers = { ...queryBuilder.headers, ...headersToSet };
      logger.log(`🔧 SecuredSupabase: Set headers for ${table}:`, headersToSet);
    }
    
    return queryBuilder;
  }


  /**
   * Enhanced from method that waits for dealer context before proceeding
   */
  async fromAsync<T extends keyof Database['public']['Tables']>(table: T) {
    const dealerHeaders = await this.getDealerHeadersAsync();
    
    const queryBuilder = this.baseClient.from(table);
    
    // Always set headers for better RLS compliance
    const headersToSet = {
      ...dealerHeaders,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    
    // Override the headers property 
    queryBuilder.headers = { ...queryBuilder.headers, ...headersToSet };
    logger.log(`🔧 SecuredSupabase: Set async headers for ${table}:`, headersToSet);
    
    return queryBuilder;
  }

  /**
   * Execute RPC with dealer context
   */
  async rpc<T = any>(
    fn: keyof Database['public']['Functions'],
    args: any = {},
    options: { head?: boolean; count?: 'exact' | 'planned' | 'estimated' } = {}
  ): Promise<{ data: T; error: any }> {
    return this.baseClient.rpc(fn as any, args, options as any);
  }
}

// Create singleton instance
export const securedSupabase = new SecuredSupabaseClient();

// Re-export base client for compatibility
export { baseSupabase as supabase };

/**
 * Hook to get current dealer context
 */
export function useDealerContext() {
  const dealerInfo = DealerManager.getDealerInfo();
  
  return {
    dealerId: dealerInfo?.dealer_id || null,
    domain: dealerInfo?.domain || null,
    isConfigured: DealerManager.isConfigured(),
    isTradeBoostEnabled: DealerManager.isTradeBoostEnabled(),
  };
}

/**
 * Utility to get dealer context headers for direct fetch requests (optional)
 */
export function getDealerHeaders(): Record<string, string> {
  try {
    const dealerInfo = DealerManager.getDealerInfo();
    
    if (dealerInfo) {
      return {
        'x-dealer-id': dealerInfo.dealer_id.toString(),
        'x-dealer-domain': dealerInfo.domain,
      };
    }
    
    return {};
  } catch (error) {
    logger.warn('Could not get dealer context:', error);
    return {};
  }
}